import React from 'react';
import { Users, GraduationCap, HeartHandshake, ShieldAlert } from 'lucide-react';
import CustomSelect from '../../../components/ui/CustomSelect';
import { Input } from '../../../components/ui/Input';
import Tooltip from '../../../components/ui/Tooltip';

export default function FoyerFiscalForm({ data, onChange, parts }) {
  const statutOptions = [
    { value: "celibataire", label: "Célibataire" },
    { value: "marie", label: "Marié(e) (Déclaration commune)" },
    { value: "pacse", label: "Pacsé(e) (Déclaration commune)" },
    { value: "divorce", label: "Divorcé(e) / Séparé(e)" },
    { value: "veuf", label: "Veuf(ve)" },
  ];

  const handleFieldChange = (field, val) => {
    onChange(prev => ({ ...prev, [field]: val }));
  };

  const isCouple = data.statut === "marie" || data.statut === "pacse";

  return (
    <div className="space-y-6">
      {/* Statut & Composition */}
      <div className="workspace-card p-6 bg-surface border border-workspace rounded-2xl shadow-hp-sm">
        <div className="flex items-center justify-between pb-4 border-b border-workspace mb-5">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-secondary" />
            <h3 className="text-base font-bold text-primary">Composition du Foyer & Quotient Familial</h3>
          </div>
          <span className="px-3.5 py-1.5 rounded-full bg-secondary/10 text-secondary text-xs font-black">
            {parts} {parts > 1 ? "parts fiscales" : "part fiscale"}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {/* Situation matrimoniale */}
          <div>
            <label className="block text-xs font-medium text-subtle mb-1.5">Situation Matrimoniale</label>
            <CustomSelect
              value={data.statut}
              onChange={(val) => handleFieldChange("statut", val)}
              options={statutOptions}
              className="w-full"
            />
          </div>

          {/* Enfants à charge exclusive */}
          <div>
            <label className="block text-xs font-medium text-subtle mb-1.5">Enfants à charge exclusive</label>
            <Input
              type="number"
              min="0"
              value={data.enfants ?? 0}
              onChange={(e) => handleFieldChange("enfants", Math.max(0, parseInt(e.target.value) || 0))}
              className="w-full"
            />
          </div>

          {/* Enfants en garde alternée */}
          <div>
            <label className="block text-xs font-medium text-subtle mb-1.5">Enfants en garde alternée</label>
            <Input
              type="number"
              min="0"
              value={data.enfantsGardeAlternee ?? 0}
              onChange={(e) => handleFieldChange("enfantsGardeAlternee", Math.max(0, parseInt(e.target.value) || 0))}
              className="w-full"
            />
          </div>
        </div>

        {/* Options spécifiques (parent isolé, invalidité) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5 pt-4 border-t border-workspace">
          <label className="flex items-center gap-3 p-3 rounded-xl bg-surface-muted/30 border border-workspace cursor-pointer hover:bg-surface-muted/60 transition">
            <input
              type="checkbox"
              checked={data.parentIsole || false}
              onChange={(e) => handleFieldChange("parentIsole", e.target.checked)}
              className="h-4 w-4 rounded border-workspace text-secondary focus:ring-secondary"
            />
            <div className="text-xs">
              <span className="font-bold text-primary block">Parent Isolé (Case T)</span>
              <span className="text-subtle">Majoration d'une demi-part si vous élevez seul vos enfants.</span>
            </div>
          </label>

          <label className="flex items-center gap-3 p-3 rounded-xl bg-surface-muted/30 border border-workspace cursor-pointer hover:bg-surface-muted/60 transition">
            <input
              type="checkbox"
              checked={data.personneInvalide || false}
              onChange={(e) => handleFieldChange("personneInvalide", e.target.checked)}
              className="h-4 w-4 rounded border-workspace text-secondary focus:ring-secondary"
            />
            <div className="text-xs">
              <span className="font-bold text-primary block">Carte Mobilité Inclusion / Invalidité</span>
              <span className="text-subtle">Majoration d'une demi-part pour personne invalide au foyer.</span>
            </div>
          </label>
        </div>
      </div>

      {/* Enfants scolarisés (Réduction d'impôt directe) */}
      <div className="workspace-card p-6 bg-surface border border-workspace rounded-2xl shadow-hp-sm">
        <div className="flex items-center justify-between pb-4 border-b border-workspace mb-5">
          <div className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-secondary" />
            <div>
              <h3 className="text-base font-bold text-primary">Enfants Scolarisés (Réduction Directe)</h3>
              <p className="text-xs text-subtle">Réduction d'impôt forfaitaire : 61 € (Collège), 153 € (Lycée), 183 € (Supérieur).</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <div>
            <label className="block text-xs font-medium text-subtle mb-1.5">Collège (61 € / enfant)</label>
            <Input
              type="number"
              min="0"
              value={data.enfantsCollege ?? 0}
              onChange={(e) => handleFieldChange("enfantsCollege", Math.max(0, parseInt(e.target.value) || 0))}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-subtle mb-1.5">Lycée (153 € / enfant)</label>
            <Input
              type="number"
              min="0"
              value={data.enfantsLycee ?? 0}
              onChange={(e) => handleFieldChange("enfantsLycee", Math.max(0, parseInt(e.target.value) || 0))}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-subtle mb-1.5">Enseignement Supérieur (183 €)</label>
            <Input
              type="number"
              min="0"
              value={data.enfantsSuperieur ?? 0}
              onChange={(e) => handleFieldChange("enfantsSuperieur", Math.max(0, parseInt(e.target.value) || 0))}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

